import { EmployeeWorkSheetDetails } from "@/model/employee-work-details/employee-worksheet-details";
import { Component, OnInit } from "@angular/core";
import {
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from "@angular/forms";
import { AppService } from "@services/app.service";
import { NgxSpinnerService } from "ngx-spinner";
import { ToastrService } from "ngx-toastr";
import { Subject, takeUntil } from "rxjs";

@Component({
  selector: "app-worksheet-entry-form",
  templateUrl: "./worksheet-entry-form.component.html",
  styleUrl: "./worksheet-entry-form.component.scss",
})
export class WorksheetEntryFormComponent implements OnInit {
  destroy$: Subject<boolean> = new Subject<boolean>();
  formId: number = 0;
  availableList: any;
  isReadOnly: boolean = false;
  wagsMonth: string = "";
  getAllWagsMonth: string[] = [];
  worksheetEntryDetails: EmployeeWorkSheetDetails[] = [];
  isCountReadyOnly: boolean = false;
  isShowAddedImage: boolean = true;
  today: any;
  salaryValue: any;
  hasIndexingError: boolean = false;

  constructor(
    private appService: AppService,
    private spinner: NgxSpinnerService,
    private toaster: ToastrService
  ) { }

  addWorksheetEntryForm = new UntypedFormGroup({
    employeeDailyWorkLogId: new UntypedFormControl(0, [
      Validators.required,
      Validators.nullValidator,
    ]),
    formId: new UntypedFormControl(0, [
      Validators.required,
      Validators.nullValidator,
    ]),
    entryDate: new UntypedFormControl(0, [
      Validators.required,
      Validators.nullValidator,
    ]),
    month: new UntypedFormControl("", [
      Validators.required,
      Validators.nullValidator,
    ]),
    employeeName: new UntypedFormControl("", [
      Validators.required,
      Validators.nullValidator,
    ]),
    noOfLinesTyped: new UntypedFormControl("", [
      Validators.required,
      Validators.nullValidator,
    ]),
    noOfImagesCompleted: new UntypedFormControl("", [
      Validators.required,
      Validators.nullValidator,
    ]),
    totalImageCount: new UntypedFormControl("", [
      Validators.required,
      Validators.nullValidator,
    ]),
    availableCount: new UntypedFormControl("", [
      Validators.required,
      Validators.nullValidator,
    ]),
    workingStatus: new UntypedFormControl("", [
      Validators.required,
      Validators.nullValidator,
    ]),
    createdAt: new UntypedFormControl(0, [
      Validators.required,
      Validators.nullValidator,
    ]),
    createdBy: new UntypedFormControl(0, [
      Validators.required,
      Validators.nullValidator,
    ]),
    updatedAt: new UntypedFormControl(0, [
      Validators.required,
      Validators.nullValidator,
    ]),
    updatedBy: new UntypedFormControl(0, [
      Validators.required,
      Validators.nullValidator,
    ]),
    assignCount: new UntypedFormControl("", [
      Validators.required,
      Validators.nullValidator,
    ]),
    isAssigned: new UntypedFormControl(false, [
      Validators.required,
      Validators.nullValidator,
    ]),
    isLeave: new UntypedFormControl(false, [
      Validators.required,
      Validators.nullValidator,
    ]),
    anyAddedImageCount: new UntypedFormControl("", [
      Validators.required,
      Validators.nullValidator,
    ]),
    noExtractableData: new UntypedFormControl(0, [
      Validators.required,
      Validators.nullValidator,
    ]),
    accountableIndexing: new UntypedFormControl(0, [
      Validators.required,
      Validators.nullValidator,
    ]),
    accountIndexingCost: new UntypedFormControl(0, [
      Validators.required,
      Validators.nullValidator,
    ]),
    pageStartingNumber: new UntypedFormControl("", [
      Validators.required,
      Validators.nullValidator,
    ]),
    pageEndingNumber: new UntypedFormControl("", [
      Validators.required,
      Validators.nullValidator,
    ]),
    batchNumber: new UntypedFormControl("", [
      Validators.required,
      Validators.nullValidator,
    ]),
  });

  public worksheetEntryFormErrors = {
    totalImageCount: "",
    entryDate: "",
    noOfLinesTyped: "",
    noOfImagesCompleted: "",
    availableCount: "",
    assignCount: "",
    noExtractableData: "",
    pageStartingNumber: "",
    pageEndingNumber: "",
    batchNumber: "",
  };

  ngOnInit() {
    this.getEmployeeWorkPendingCount();
    this.getEmployeeSalaryValue();
    const decryptedFormId = atob(localStorage.getItem("formId") || "");
    this.formId = Number(decryptedFormId);
    this.addWorksheetEntryForm.controls["formId"].setValue(this.formId);
    const today = new Date();
    this.today = today.toLocaleDateString("en-GB"); // returns "dd/MM/yyyy"
    this.today = today.toISOString().split("T")[0];
    console.log(this.today);
    this.addWorksheetEntryForm.controls["entryDate"]?.setValue(this.today);

    this.addWorksheetEntryForm.get('isAssigned')?.valueChanges.subscribe(value => {
      const assignCountControl = this.addWorksheetEntryForm.get('assignCount');

      if (value === true) {
        assignCountControl?.addValidators([Validators.required]);
      } else {
        assignCountControl?.removeValidators([Validators.required]);
        assignCountControl?.setValue("");   // optional: clear value
      }

      assignCountControl?.updateValueAndValidity();
    });
  }

  numberOnly(event: any): boolean {
    const charCode = event.which ? event.which : event.keyCode;
    if (charCode > 31 && (charCode < 48 || charCode > 57)) {
      return false;
    }
    return true;
  }

  onSubmit() {
    this.formValidation();
  }

  formValidation() {
    // Reset error messages
    this.worksheetEntryFormErrors = {
      totalImageCount: "",
      entryDate: "",
      noOfImagesCompleted: "",
      noOfLinesTyped: "",
      availableCount: "",
      assignCount: "",
      noExtractableData: "",
      pageStartingNumber: "",
      pageEndingNumber: "",
      batchNumber: "",
    };

    let hasError = false;

    const entryDate = this.addWorksheetEntryForm.get("entryDate")?.value;
    const isLeave = this.addWorksheetEntryForm.get("isLeave")?.value;
    const isAssigned = this.addWorksheetEntryForm.get("isAssigned")?.value;

    // Entry date validation (common for both cases)
    if (!entryDate) {
      this.worksheetEntryFormErrors.entryDate = "*Date is required";
      this.addWorksheetEntryForm.get("entryDate")?.markAsTouched();
      hasError = true;
    }

    // Case 1: Leave or Assigned is true
    if (isLeave || isAssigned) {
      if (isAssigned) {
        if (this.addWorksheetEntryForm.get("assignCount")?.invalid) {
          this.worksheetEntryFormErrors.assignCount =
            "*Assign image count is required";
          this.addWorksheetEntryForm.get("assignCount")?.markAsTouched();
          hasError = true;
        }
      }
      if (!hasError) {
        // Set specific values to 0
        this.addWorksheetEntryForm.patchValue({
          noOfImagesCompleted: 0,
          noOfLinesTyped: 0,
        });
        this.saveWorkSheetDetails();
      }

      return;
    }

    // Case 2: Neither leave nor assigned is true – full validation
    if (this.addWorksheetEntryForm.get("totalImageCount")?.invalid) {
      this.worksheetEntryFormErrors.totalImageCount =
        "*Total given images is required";
      this.addWorksheetEntryForm.get("totalImageCount")?.markAsTouched();
      hasError = true;
    }

    if (this.addWorksheetEntryForm.get("pageStartingNumber")?.invalid) {
      this.worksheetEntryFormErrors.pageStartingNumber =
        "*Page starting number is required";
      this.addWorksheetEntryForm.get("pageStartingNumber")?.markAsTouched();
      hasError = true;
    }

    if (this.addWorksheetEntryForm.get("pageEndingNumber")?.invalid) {
      this.worksheetEntryFormErrors.pageEndingNumber =
        "*Page ending number is required";
      this.addWorksheetEntryForm.get("pageEndingNumber")?.markAsTouched();
      hasError = true;
    }

    if (this.addWorksheetEntryForm.get("batchNumber")?.invalid) {
      this.worksheetEntryFormErrors.batchNumber =
        "*Batch number is required";
      this.addWorksheetEntryForm.get("batchNumber")?.markAsTouched();
      hasError = true;
    }

    if (this.addWorksheetEntryForm.get("noOfImagesCompleted")?.invalid) {
      this.worksheetEntryFormErrors.noOfImagesCompleted =
        "*Completed images is required";
      this.addWorksheetEntryForm.get("noOfImagesCompleted")?.markAsTouched();
      hasError = true;
    }

    if (this.addWorksheetEntryForm.get("availableCount")?.invalid) {
      this.worksheetEntryFormErrors.availableCount =
        "*Pending images is required";
      this.addWorksheetEntryForm.get("availableCount")?.markAsTouched();
      hasError = true;
    }

    if (this.addWorksheetEntryForm.get("noOfLinesTyped")?.invalid) {
      this.worksheetEntryFormErrors.noOfLinesTyped =
        "*Total indexing/images is required";
      this.addWorksheetEntryForm.get("noOfLinesTyped")?.markAsTouched();
      hasError = true;
    }

    if (this.addWorksheetEntryForm.get("noExtractableData")?.invalid) {
      this.worksheetEntryFormErrors.noExtractableData =
        "*No extractable data is required";
      this.addWorksheetEntryForm.get("noExtractableData")?.markAsTouched();
      hasError = true;
    }

    // Custom validation for image counts
    const completed = Number(
      this.addWorksheetEntryForm.get("noOfImagesCompleted")?.value || 0
    );
    const total = Number(
      this.addWorksheetEntryForm.get("totalImageCount")?.value || 0
    );
    const available = Number(
      this.addWorksheetEntryForm.get("availableCount")?.value || 0
    );

    if (this.isReadOnly && completed > this.availableList?.availableCount) {
      this.worksheetEntryFormErrors.noOfImagesCompleted =
        "*Completed images cannot exceed pending images";
      hasError = true;
    }

    if (!this.isReadOnly && completed > total) {
      this.worksheetEntryFormErrors.noOfImagesCompleted =
        "*Completed images cannot exceed total image count";
      hasError = true;
    }

    if (this.hasIndexingError) {
      this.worksheetEntryFormErrors.noExtractableData =
        "*No Extractable Data cannot exceed Total Indexing";
      return;
    }

    // Final save decision
    if (!hasError) {
      this.saveWorkSheetDetails();
    }
  }

  /*   Get employee pending count */
  getEmployeeWorkPendingCount() {
    this.appService
      .getPendingTicketCount()
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        (data) => {
          this.availableList = data;
          console.log(data);
          this.spinner.hide();
          if (this.availableList?.availableCount == null) {
            this.addWorksheetEntryForm.controls["availableCount"].setValue(
              this.availableList?.availableCount || 0
            );
            this.addWorksheetEntryForm.controls["totalImageCount"].setValue(
              this.availableList?.availableCount
            );
            this.addWorksheetEntryForm.controls["pageStartingNumber"].setValue(
              this.availableList?.pageStartingNumber
            );
            this.addWorksheetEntryForm.controls["pageEndingNumber"].setValue(
              this.availableList?.pageEndingNumber
            );
            this.addWorksheetEntryForm.controls["batchNumber"].setValue(
              this.availableList?.batchNumber
            );
            this.isReadOnly = false;
            this.isCountReadyOnly = true;
          } else if (this.availableList?.availableCount != 0) {
            this.addWorksheetEntryForm.controls["availableCount"].setValue(
              this.availableList?.availableCount || 0
            );
            this.addWorksheetEntryForm.controls["totalImageCount"].setValue(
              this.availableList?.availableCount
            );
            this.addWorksheetEntryForm.controls["pageStartingNumber"].setValue(
              this.availableList?.pageStartingNumber
            );
            this.addWorksheetEntryForm.controls["pageEndingNumber"].setValue(
              this.availableList?.pageEndingNumber
            );
            this.addWorksheetEntryForm.controls["batchNumber"].setValue(
              this.availableList?.batchNumber
            );
            this.isReadOnly = true;
            this.isCountReadyOnly = false;
          } else if (this.availableList?.availableCount == 0) {
            this.isReadOnly = false;
            this.isCountReadyOnly = false;
          }
        },
        (error: any) => {
          this.toaster.error(
            error.error.message || "Something went wrong,please try again....."
          );
          this.spinner.hide();
        }
      );
  }

  /*   Get employee salary value */
  getEmployeeSalaryValue() {
    this.spinner.show();
    this.appService
      .dataEntryEmployeeSalaryValue()
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        (data) => {
          this.salaryValue = data;
          this.spinner.hide();
        },
        (error: any) => {
          this.toaster.error(
            error.error.message || "Something went wrong,please try again....."
          );
          this.spinner.hide();
        }
      );
  }

  //Calculate add image based pending count updating
  addedImageCount(event: any): void {
    const addedValue = Number(event.target.value) || 0;
    this.addWorksheetEntryForm.controls["anyAddedImageCount"].setValue(
      addedValue
    );

    // Recalculate pending when this value changes
    this.calculatePendingImages();
  }

  enteredTotalImage(event: any) {
    const value = +event.target.value;
    if (value == 0) {
      this.isCountReadyOnly = true;
    } else {
      this.isCountReadyOnly = false;
    }
  }

  OnCalculate(event: any): void {
    const completedValue = Number(event.target.value) || 0;
    this.addWorksheetEntryForm.controls["noOfImagesCompleted"].setValue(
      completedValue
    );

    // Reset error messages
    this.worksheetEntryFormErrors.noOfImagesCompleted = "";
    this.worksheetEntryFormErrors.availableCount = "";

    this.calculatePendingImages();
  }

  calculatePendingImages(): void {
    const total = Number(
      this.addWorksheetEntryForm.get("totalImageCount")?.value || 0
    );
    const added = Number(
      this.addWorksheetEntryForm.get("anyAddedImageCount")?.value || 0
    );
    const completed = Number(
      this.addWorksheetEntryForm.get("noOfImagesCompleted")?.value || 0
    );

    const totalAvailable = total + added;

    // Validation
    if (this.isReadOnly) {
      if (completed > totalAvailable) {
        this.worksheetEntryFormErrors.noOfImagesCompleted =
          "*Completed images cannot exceed pending images";
        this.addWorksheetEntryForm.controls["availableCount"].setValue("");
        return;
      }
    } else {
      if (completed > totalAvailable) {
        this.worksheetEntryFormErrors.noOfImagesCompleted =
          "*Completed images cannot exceed total + added images";
        this.addWorksheetEntryForm.controls["availableCount"].setValue("");
        return;
      }
    }

    const pending = totalAvailable - completed;
    this.addWorksheetEntryForm.controls["availableCount"].setValue(pending);
  }

  OnCalculateIndexing(): void {
    const totalIndexing = Number(
      this.addWorksheetEntryForm.get("noOfLinesTyped")?.value || 0
    );
    const noExtractable = Number(
      this.addWorksheetEntryForm.get("noExtractableData")?.value || 0
    );

    this.worksheetEntryFormErrors.noExtractableData = "";
    this.hasIndexingError = false;

    if (noExtractable > totalIndexing) {
      this.worksheetEntryFormErrors.noExtractableData =
        "*No Extractable Data cannot exceed Total Indexing";
      this.addWorksheetEntryForm.get("accountableIndexing")?.setValue(0);
      this.addWorksheetEntryForm.get("accountIndexingCost")?.setValue(0);
      this.hasIndexingError = true; // Set error flag
      return;
    }

    const accountableIndexing = totalIndexing - noExtractable;
    this.addWorksheetEntryForm
      .get("accountableIndexing")
      ?.setValue(accountableIndexing);

    const cost = accountableIndexing * this.salaryValue;
    this.addWorksheetEntryForm.get("accountIndexingCost")?.setValue(cost);
  }

  onSelectLeaveStatus(status: boolean) {
    this.handleStatusChange(status);
  }

  onSelectAssignedStatus(status: boolean) {
    // Toggle image display and reset value if not assigned
    this.isShowAddedImage = !status;

    if (!status) {
      this.addWorksheetEntryForm.controls["anyAddedImageCount"].setValue("");
    }

    this.handleStatusChange(status);
  }

  // Common handler
  handleStatusChange(status: boolean) {
    const isLeave = this.addWorksheetEntryForm.get("isLeave")?.value;
    const isAssigned = this.addWorksheetEntryForm.get("isAssigned")?.value;

    // Make fields editable only if both are false
    this.isCountReadyOnly = isLeave || isAssigned;

    if (this.isCountReadyOnly) {
      // Reset form fields when read-only
      this.addWorksheetEntryForm.patchValue({
        noOfImagesCompleted: "",
        noOfLinesTyped: "",
        noExtractableData: 0,
        anyAddedImageCount: "",
        accountableIndexing: 0,
        accountIndexingCost: 0,
      });

      // Reset field status
      this.addWorksheetEntryForm.get("noOfImagesCompleted")?.markAsPristine();
      this.addWorksheetEntryForm.get("noOfLinesTyped")?.markAsPristine();
    }
  }

  /* Save employee worksheet entry */
  saveWorkSheetDetails() {
    // Get the original date from the form
    const originalDate: Date =
      this.addWorksheetEntryForm.get("entryDate")?.value;

    // Convert to 13-digit epoch (milliseconds)
    const epochDate = new Date(originalDate).getTime();
    this.addWorksheetEntryForm.controls["entryDate"].setValue(epochDate);
    this.spinner.show();
    this.appService
      .saveWorkSheet(this.addWorksheetEntryForm.value)
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        (data) => {
          this.toaster.success("Details saved successfully......");
          this.addWorksheetEntryForm.reset();
          this.addWorksheetEntryForm.controls["entryDate"]?.setValue(
            this.today
          );
          this.addWorksheetEntryForm.controls["isLeave"]?.setValue(false);
          this.addWorksheetEntryForm.controls["isAssigned"]?.setValue(false);
          this.getEmployeeWorkPendingCount();
          this.isShowAddedImage = true;
        },
        (error: any) => {
          this.addWorksheetEntryForm.controls["entryDate"].setValue(
            originalDate
          );
          this.toaster.error(
            error.error.message || "Something went wrong,please try again....."
          );
          this.spinner.hide();
        }
      );
  }
}
